//#region Util Imports

import * as _ from 'underscore'
import moment = require('moment')

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import { locale as globalizeLocale } from 'globalize'
import { dxSelectBoxOptions } from 'devextreme/ui/select_box'
import dxValidator, { dxValidatorOptions } from 'devextreme/ui/validator'
import { dxFormSimpleItem } from 'devextreme/ui/form'
import { dxDropDownBoxOptions } from 'devextreme/ui/drop_down_box'

//#endregion

//#region IMR Imports

import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

//#endregion

import { UserSessionService } from "../../../../../services/user-session"

import { ConfigHelper, LocalizationHelper, TypeHelper } from "../../../../../helpers"

import { Enums } from "../../../../../models/common"

import { IMRBaseControlController } from "../../../base"

import { FieldModel, CorrespondingActionTypeModel, ColumnModel } from '../../../../api/api.models'
import { AlzActionOptions, IAlzAction } from './action-run.dedicated.alz-action.interfaces'
import { CreateEditFieldSettings, CreateEditSection, ExtendedFormItem } from '../../../common.create-edit'
import { AlzActionService } from './services/alz-action.service'
import { OpActionData } from '../../../../../models/core'
import { DateBoxControlsService } from '../../../../../services/controls'
import { ReferenceTypeService } from '../../../../../services/reference-type'
import { DataProviderListResult } from '../../../../../data-provider'
import { IReferenceType } from '../../../../../models/util/interfaces'

enum WorkingTypeEnum {
    astronomicalMode = 0,
    photoMode = 1,
    hybrydMode = 2,
    dimmingMode = 3
}

export class AlzActionController extends IMRBaseControlController<IAlzAction, AlzActionOptions> implements ng.IComponentController, IAlzAction {

    public get LOG_PREFIX(): string { return '[SIMAX.Common.ACTION-RUN.DEDICATED.ALZ-ACTION]' }

    public popupOptions: IMRPopupOptions
    public showLoader: boolean = false
    public panels: CreateEditSection[]
    public formData: { [key: number]: any }
    public inputData: { [key: number]: any }

    public showActionRunOptions: boolean
    public readonly astronomicalMode: number = WorkingTypeEnum.astronomicalMode
    public readonly photoMode: number = WorkingTypeEnum.photoMode
    public readonly hybrydMode: number = WorkingTypeEnum.hybrydMode
    public readonly dimmingMode: number = WorkingTypeEnum.dimmingMode

    public readonly astronomicalModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AstronomicalModeExplain)
    public readonly photoModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PhotoModeExplain)
    public readonly hybrydModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.HybrydModeExplain)
    public readonly dimmingModeExplainText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DimmingModeExplain)
    public readonly operatingModeText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.OperatingMode)

    public operatingModeSelectBoxOptions: dxSelectBoxOptions
    public operatingModeValidatorOptions: dxValidatorOptions
    public operationalModeValue: WorkingTypeEnum

    public timeRangeSource: { id: number, caption: string }[] = []

    private action: CorrespondingActionTypeModel
    private popup: IIMRPopup
    private operationalModeValidator: dxValidator

    private referenceTableCount: number = 0
    private readonly workingTypeFieldId: number = 0
    private readonly workingTypes: { id: WorkingTypeEnum, caption: string }[] = [{
        id: WorkingTypeEnum.astronomicalMode, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AstronomicalMode)
    }, {
        id: WorkingTypeEnum.photoMode, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PhotocellMode)
    }, {
        id: WorkingTypeEnum.hybrydMode, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.HybridMode)
    }, {
        id: WorkingTypeEnum.dimmingMode, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DimmingMode)
    }]

    constructor(
        private readonly userSessionService: UserSessionService,
        private readonly alzActionService: AlzActionService,
        protected readonly referenceTypeService: ReferenceTypeService,
        private readonly dateBoxControlsService: DateBoxControlsService
    ) {
        super()
    }

    protected initialize(options: AlzActionOptions): JQueryPromise<any> {
        this.setAction(options.action)

        return $
            .when(
                this.initializepPopupOptions()
            )
    }

    // #region Popup

    private initializepPopupOptions(): JQueryPromise<any> {
        this.popupOptions = {

            innerOptions: {
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlzAction),
                deferRendering: false,
                onHiding: () => this.onPopupHiding(),
                fullScreen: false,
                maxWidth: 700,
                maxHeight: 800
            },

            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.initializePopup(e.component)
            },

            bottomToolbarOptions: {
                items: [
                    {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SendAction),
                            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SendAction),
                            onClick: () => {
                                this.onSubmit()
                            }
                        }
                    }, {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                            onClick: () => {
                                this.popup.hide()
                            },
                        }
                    }
                ]
            }
        }

        return super.waitForOnInitialized(this.popupOptions)
    }

    // #endregion

    // #region Private Methods

    private onPopupHiding(): void {
        this.showLoader = false

        if (_.isFunction(this.options.onHiding)) {
            this.options.onHiding()
        }

        this.clear()
    }

    private clear(): void {
        this.panels = null
        this.formData = null
        this.inputData = null
    }

    private initializePopup(component: IIMRPopup): void {
        this.popup = component
    }

    private onSubmit(): void {
        if (_.isFunction(this.options.onActionRunClick)) {
            if (this.panels.filter((panel: CreateEditSection) => panel.form != null).every((panel: CreateEditSection) => panel.form.validate().isValid) === true &&
                this.operationalModeValidator.validate().isValid == true) {

                switch (this.operationalModeValue) {
                    case WorkingTypeEnum.astronomicalMode:
                        this.formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] = true
                        this.formData[Enums.DataType.LIGHT_SENSOR_ENABLED] = false
                        break

                    case WorkingTypeEnum.photoMode:
                        this.formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] = false
                        this.formData[Enums.DataType.LIGHT_SENSOR_ENABLED] = true
                        break

                    case WorkingTypeEnum.hybrydMode:
                        this.formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] = true
                        this.formData[Enums.DataType.LIGHT_SENSOR_ENABLED] = true
                        break

                    case WorkingTypeEnum.dimmingMode:
                        this.formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] = false
                        this.formData[Enums.DataType.LIGHT_SENSOR_ENABLED] = false
                        break
                }

                this.action.fields = []
                _.forEach(this.panels, (panel: CreateEditSection) => {
                    for (let field in panel.formData) {
                        const item = _.find(panel.items, (item: ExtendedFormItem) => item.dataField == field)

                        if (item != null && item.visible == true || (+field == Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED || +field == Enums.DataType.LIGHT_SENSOR_ENABLED)) {
                            if (_.isBoolean(panel.formData[field]) || !TypeHelper.isNullOrEmpty(panel.formData[field])) {
                                const fieldItem: FieldModel = new FieldModel()
                                fieldItem.idDataType = +field
                                fieldItem.index = 0
                                fieldItem.value = panel.formData[field]

                                if (fieldItem.idDataType == Enums.DataType.DALI_LAMP_OFF_MINUTES || fieldItem.idDataType == Enums.DataType.DALI_LAMP_ON_MINUTES) {
                                    const date: Date = panel.formData[field]
                                    const value: number = date.getHours() * 60 + date.getMinutes()
                                    fieldItem.value = value
                                }

                                this.action.fields.push(fieldItem)
                            }
                        }
                    }
                })

                this.options.onActionRunClick(this.action)
                this.popup.showLoading()
            }
        }
    }

    // #endregion


    // #region IAlzaction

    public showPopup(): void {
        this.popup.show()
        this.popup.showLoading()
        this.prepareForm()
    }

    public setAction(action: CorrespondingActionTypeModel): void {
        this.action = TypeHelper.clone(action, true)
    }

    public hidePopup(): void {
        this.popup.hide()
    }

    // #endregion

    //#region Form

    private prepareForm(): void {
        //FORM

        this.userSessionService
            .getDataProvider()
            .Action
            .getActionParameters(this.action.id, this.action.type, this.action.referenceValues[0])
            .then((params: OpActionData[]) => {
                const idDataTypeFromParameters: number[] = []

                //#region Form data 
                const formData: { [key: number]: any } = {}
                _.forEach(params, (param: OpActionData) => {
                    if (param.idDataType == Enums.DataType.DALI_LAMP_OFF_MINUTES || param.idDataType == Enums.DataType.DALI_LAMP_ON_MINUTES) {
                        if (param.value != null) {
                            let date: Date = new Date()
                            date.setHours(0, 0, 0, 0)
                            date = new Date(date.getTime() + param.value * 60000)
                            formData[param.idDataType] = date
                        }
                    } else {
                        formData[param.idDataType] = param.value
                    }

                    idDataTypeFromParameters.push(param.idDataType)
                })

                if (formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] != null && formData[Enums.DataType.LIGHT_SENSOR_ENABLED] != null) {
                    if (formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] == true && formData[Enums.DataType.LIGHT_SENSOR_ENABLED] == false) {
                        formData[this.workingTypeFieldId] = WorkingTypeEnum.astronomicalMode
                    } else if (formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] == false && formData[Enums.DataType.LIGHT_SENSOR_ENABLED] == true) {
                        formData[this.workingTypeFieldId] = WorkingTypeEnum.photoMode
                    } else if (formData[Enums.DataType.ASTRONOMICAL_CLOCK_ENABLED] == true && formData[Enums.DataType.LIGHT_SENSOR_ENABLED] == true) {
                        formData[this.workingTypeFieldId] = WorkingTypeEnum.hybrydMode
                    } else {
                        formData[this.workingTypeFieldId] = WorkingTypeEnum.dimmingMode
                    }
                }

                this.operationalModeValue = formData[this.workingTypeFieldId]
                this.formData = formData
                this.inputData = TypeHelper.clone(formData, true)
                //#endregion                

                this.loadColumns(idDataTypeFromParameters)
                    .then((panels: CreateEditSection[]) => {
                        this.panels = []

                        panels.forEach((section: CreateEditSection) => {
                            switch (section.type) {
                                default:
                                case "form":
                                    this.prepareOperatingModeSelectBoxOptions(section)
                                    this.createForm(section, this.formData)
                                    break
                            }
                            this.panels.push(section)
                        })
                    })
            })
    }

    private loadColumns(additionalDataTypes: number[]): JQueryPromise<CreateEditSection[]> {
        return this.alzActionService
            .getFormColumns(additionalDataTypes)
            .then((columns: CreateEditFieldSettings[]) => {
                const dict: CreateEditSection = <CreateEditSection>{
                    name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Basics),
                    type: "form",
                    columns: columns
                }

                return [dict]
            })
    }

    private createForm(section: CreateEditSection, data: any): void {
        section.formData = {}
        section.onFieldChanged = (e: any) => {
            if (e.dataField) {
                if (!isNaN(parseFloat(e.value))) {
                    section.formData[e.dataField] = parseFloat(e.value)
                } else {
                    section.formData[e.dataField] = e.value
                }

                this.trackChanges(section, e.dataField, e.value)
            } else {
                const value: any = (e.selectedItem) ? e.selectedItem.id : ''
                const index: string = e.element.get(0).attributes['field-id'].nodeValue
                section.formData[index] = value
                this.trackChanges(section, index, value)
            }
        }

        section.onDateFieldChanged = (e) => {
            const index: string = e.element.get(0).attributes['field-id'].nodeValue
            this.trackChanges(section, index, e.value)
        }

        let formStateUpdated: boolean = false

        section.options = <DevExpress.ui.dxFormOptions>{
            onFieldDataChanged: (e: any) => {
                this.trackChanges(section, e.dataField, e.value)
            },
            onOptionChanged: (e: any) => {
                if (e.name == 'items' && formStateUpdated == false) {
                    formStateUpdated = true
                    this.onWorkingTypeValueChanged(this.operationalModeValue, section)
                }
            },
            requiredMark: '*',
            showRequiredMark: true,
            labelLocation: 'top',
            colCount: 2,
            width: '100%',
            height: '100%',
            scrollingEnabled: true,
            bindingOptions: {
                formData: 'panel.formData',
                items: 'panel.items'
            },
            onInitialized: e => {
                section.form = e.component
                this.loadForm(section, data)
                this.popup.hideLoading()
            }
        }

        if (section.form) {
            this.loadForm(section, data)
        }
    }

    /**tracks changes between sections */
    private trackChanges(section: CreateEditSection, index: string, value: any): void {
        this.panels.forEach((panel: CreateEditSection) => {
            if (panel != section) {
                if (panel.form) {
                    panel.formData[index] = value
                }
            }
        })
    }

    private loadForm(section: CreateEditSection, data: any): void {
        section.form.beginUpdate()
        section.items = []
        section.formData = data != null ? data : {}
        const items: ExtendedFormItem[] = section.items

        for (let i = 0; i < section.columns.length; ++i) {
            const column: CreateEditFieldSettings = section.columns[i]

            items.push({
                dataField: column.id.toString(),
                visible: true,
                label: {
                    text: column.caption
                },
                helpText: column.hint,
                validationRules: [{
                    type: 'pattern',
                    pattern: ''
                }]
            })

            if (section.required) {
                for (let j = 0; j < section.required.length; j++) {
                    const req: CreateEditFieldSettings = section.required[j]
                    if (req.id == column.id && column.type != Enums.DataTypeClass.Boolean) {
                        items[items.length - 1].validationRules.push({
                            type: 'required',
                            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RequiredField)
                        })
                        break
                    }
                }
            }

            this.addValidationRules(section, column, items[items.length - 1])
        }

        section.items = <any>items
        section.form.endUpdate()
    }

    private addValidationRules(section, column: CreateEditFieldSettings, item: dxFormSimpleItem = null): void {
        item.editorOptions = {}

        if (!item.validationRules) {
            item.validationRules = []
        }

        switch (column.type) {
            case Enums.DataTypeClass.Integer:
                const referenceType = column.referenceTypeId

                if (!referenceType) {
                    if (item) {
                        item.validationRules.push({
                            type: 'pattern',
                            pattern: '^-?[0-9]{0,}$',
                            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.IntegerField)
                        })

                        if (column.id == Enums.DataType.ASTRONOMICAL_CLOCK_SUNRISE_OFFSET ||
                            column.id == Enums.DataType.ASTRONOMICAL_CLOCK_SUNSET_OFFSET ||
                            column.id == Enums.DataType.DALI_LIGHT_SENSOR_SUNRISE_TIME_RANGE ||
                            column.id == Enums.DataType.DALI_LIGHT_SENSOR_SUNSET_TIME_RANGE) {

                            this.timeRangeSource = []
                            for (let i = -30; i < 31; i++) {
                                this.timeRangeSource.push({ id: i, caption: i.toString() })
                            }

                            const selectText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select)
                            const noDataText: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData)

                            item.template = '<div field-id="' + column.id + '" id="table' + column.id + '" dx-select-box="{dataSource: $ctrl.timeRangeSource, placeholder:' + "'" + selectText + "'" + ', onSelectionChanged: panel.onFieldChanged, onValueChanged: panel.onFieldValueChanged, onInitialized: panel.initializedHandler, bindingOptions: { value: \'panel.formData[' + column.id + ']\' },  valueExpr: \'id\', searchEnabled: true, displayExpr: \'caption\',  noDataText: ' + "'" + noDataText + "'" + ' }"></div>'
                        }

                        if (column != null && column.format != null) {

                            if (column.format.numberMaxValue) {
                                item.validationRules.push({
                                    type: 'range',
                                    max: column.format.numberMaxValue,
                                    message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberMaxValue) + ' ' + column.format.numberMaxValue
                                })
                            }
                            if (column.format.numberMinValue) {
                                item.validationRules.push({
                                    type: 'range',
                                    min: column.format.numberMinValue,
                                    message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberMinValue) + ' ' + column.format.numberMinValue
                                })
                            }
                        }
                    }
                    break
                } else if (referenceType && !item.template) {
                    this.getReferenceTable(referenceType, column.id)
                        .then(table => {
                            if (table && table.rows) {
                                this.referenceTableCount += 1
                                this['referenceTable' + this.referenceTableCount] = table.rows
                                var selectText = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select)
                                var noDataText = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData)

                                if (
                                    table.rows.length == 1
                                    && (!this.panels[0].formData[column.id] || this.panels[0].formData[column.id].length == 0)
                                ) {
                                    this.panels[0].formData[column.id] = table[0].id
                                }

                                item.template = '<div field-id="' + column.id + '" id="table' + column.id + '" dx-select-box="{dataSource: referenceTable' + this.referenceTableCount + ', placeholder:' + "'" + selectText + "'" + ', onSelectionChanged: panel.onFieldChanged, onValueChanged: panel.onFieldValueChanged, onInitialized: panel.initializedHandler, value: panel.formData[' + column.id + '], valueExpr: \'id\', searchEnabled: true, displayExpr: \'caption\', noDataText: ' + "'" + noDataText + "'" + ' }"></div>'
                            }
                        })
                }
                break
            case Enums.DataTypeClass.Text:
                if (column.id == Enums.DataType.HELPER_PASSWORD) {
                    item.editorOptions.mode = 'password'
                }
                if (item) {
                    if (column != null && column.format != null && column.format.textMinLength) {
                        item.validationRules.push({
                            type: 'stringLength',
                            min: column.format.textMinLength,
                            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.MinNumberOfCharacters) + ' ' + column.format.textMinLength
                        })
                    }

                    if (column != null && column.format != null && column.format.textMaxLength) {
                        item.validationRules.push({
                            type: 'stringLength',
                            max: column.format.textMaxLength,
                            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.MaxNumberOfCharacters) + ' ' + column.format.textMaxLength
                        })
                    }
                }
                break
            case Enums.DataTypeClass.Datetime:
            case Enums.DataTypeClass.Date:
                const dateBoxOptions: DevExpress.ui.dxDateBoxOptions = this.dateBoxControlsService.getDateBoxOptions({
                    format: ConfigHelper.getDateTimeFormat(ConfigHelper.DateFormat.DATE_FORMAT_DX, column.id)
                })

                section.formData[column.id] = dateBoxOptions.value
                item.template = '<div field-id="' + column.id + '" dx-date-box="{ displayFormat: ' + "'" + dateBoxOptions.displayFormat + "'" + ', type:' + "'" + dateBoxOptions.type + "'" + ', bindingOptions: { value: \'panel.formData[' + column.id + ']\' }, onValueChanged: panel.onDateFieldChanged }"></div>'

                break
            case Enums.DataTypeClass.Time:
                item.template = '<div field-id="' + column.id + '" dx-date-box="{ acceptCustomValue: false, displayFormat: dateFormat, type:' + "'time'" + ', bindingOptions: { value: \'panel.formData[' + column.id + ']\' }, onValueChanged: panel.onDateFieldChanged }"></div>'
                break
            case Enums.DataTypeClass.Real:
            case Enums.DataTypeClass.Decimal:
                if (item) {
                    const comma: string = globalizeLocale().main('numbers/symbols-numberSystem-latn/decimal')
                    const pattern: string = '^-?[0-9]{0,}((\\' + comma + '{1})[0-9]{0,})?$'

                    item.editorType = 'dxTextBox'

                    item.validationRules.push({
                        type: 'pattern',
                        pattern: pattern,
                        message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumericField)
                    })
                    if (column != null && column.format != null) {

                        if (column.format.numberMaxValue) {
                            item.validationRules.push({
                                type: 'range',
                                max: column.format.numberMaxValue,
                                message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberMaxValue) + ' ' + column.format.numberMaxValue
                            })
                        }
                        if (column.format.numberMinValue) {
                            item.validationRules.push({
                                type: 'range',
                                min: column.format.numberMinValue,
                                message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberMinValue) + ' ' + column.format.numberMinValue
                            })
                        }
                        if (column.format.numberMinPrecision && column.format.numberMaxPrecision) {
                            item.validationRules.push({
                                type: 'pattern',
                                pattern: '^\\d+\\.\\d{' + column.format.numberMinPrecision + ',' + column.format.numberMaxPrecision + '}$',
                                message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberPrecisionRange) + ' ' + column.format.numberMinPrecision + '-' + column.format.numberMaxPrecision
                            })
                        }
                        else if (column.format.numberMaxPrecision) {
                            item.validationRules.push({
                                type: 'pattern',
                                pattern: '^\\d+(\\.\\d{1,' + column.format.numberMaxPrecision + '})?$',
                                message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberMaxPrecision) + ' ' + column.format.numberMaxPrecision
                            })
                        }
                        else if (column.format.numberMinPrecision) {
                            item.validationRules.push({
                                type: 'pattern',
                                pattern: '^\\d+\\.\\d{' + column.format.numberMinPrecision + ',}$',
                                message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberMinPrecision) + ' ' + column.format.numberMinPrecision
                            })
                        }
                    }
                }
                break
            case Enums.DataTypeClass.Boolean:
                section.formData[column.id] = false

                if (item) {
                    item.validationRules[0] = {
                        type: 'pattern',
                        pattern: ''
                    }
                }
                break
            default:
                break
        }
    }

    private getReferenceTable(referenceType: Enums.ReferenceType, idDataType: number): JQueryPromise<{ rows: ColumnModel[], referenceTableName: string }> {
        return this.referenceTypeService
            .getReferenceObjects(referenceType)
            .then((result: DataProviderListResult<IReferenceType>) => {
                const referenceTableUniqueId = idDataType + '_' + referenceType
                const referenceTableName = 'referenceTable' + referenceTableUniqueId

                this[referenceTableName] = _.map(result.objects, referenceObject => {
                    return {
                        id: referenceObject.getReferenceKey(),
                        caption: referenceObject.toString()
                    }
                });

                return {
                    rows: <ColumnModel[]>this[referenceTableName],
                    referenceTableName
                }
            })

    }

    private onWorkingTypeValueChanged(selectedType: WorkingTypeEnum, section: CreateEditSection) {
        if (section.form != null) {
            section.form.beginUpdate()
            switch (selectedType) {
                case WorkingTypeEnum.astronomicalMode:
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNRISE_OFFSET}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNSET_OFFSET}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_OFF_MINUTES}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_ON_MINUTES}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.LIGHT_SENSOR_ON_LUMINOSITY_LEVEL}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNRISE_TIME_RANGE}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNSET_TIME_RANGE}`, 'visible', false)

                    break

                case WorkingTypeEnum.photoMode:
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNRISE_OFFSET}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNSET_OFFSET}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_OFF_MINUTES}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_ON_MINUTES}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.LIGHT_SENSOR_ON_LUMINOSITY_LEVEL}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNRISE_TIME_RANGE}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNSET_TIME_RANGE}`, 'visible', false)

                    break
                case WorkingTypeEnum.hybrydMode:
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNRISE_OFFSET}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNSET_OFFSET}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_OFF_MINUTES}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_ON_MINUTES}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.LIGHT_SENSOR_ON_LUMINOSITY_LEVEL}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNRISE_TIME_RANGE}`, 'visible', true)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNSET_TIME_RANGE}`, 'visible', true)

                    break

                case WorkingTypeEnum.dimmingMode:
                default:
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNRISE_OFFSET}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.ASTRONOMICAL_CLOCK_SUNSET_OFFSET}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_OFF_MINUTES}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LAMP_ON_MINUTES}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.LIGHT_SENSOR_ON_LUMINOSITY_LEVEL}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNRISE_TIME_RANGE}`, 'visible', false)
                    section.form.itemOption(`${Enums.DataType.DALI_LIGHT_SENSOR_SUNSET_TIME_RANGE}`, 'visible', false)
                    break
            }
            section.form.endUpdate()
        }
    }

    private prepareOperatingModeSelectBoxOptions(section: CreateEditSection) {
        this.operatingModeSelectBoxOptions = {
            dataSource: this.workingTypes,
            displayExpr: 'caption',
            valueExpr: 'id',
            hint: `${this.astronomicalModeExplainText}\n${this.photoModeExplainText}\n${this.hybrydModeExplainText}\n${this.dimmingModeExplainText}`,
            // showClearButton: true,
            bindingOptions: {
                value: '$ctrl.operationalModeValue',
            },
            onValueChanged: (e: any) => {
                this.onWorkingTypeValueChanged(e.value, section)
            }
        }

        this.operatingModeValidatorOptions = {
            validationRules: [{
                type: "required",
                message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RequiredField)
            }],
            onInitialized: (e: any) => {
                this.operationalModeValidator = e.component
            }
        }
    }

    //#endregion

}